import { v4 as uuidv4 } from 'uuid'
import { AbortError, getRequest, postRequest, uploadFileRequest } from 'features/common'
import { encodeUriParams } from 'features/common/utils/searchParamsUtils'
import settings from 'settings'
import {
  JobStatusType,
  ImportingJob,
  ImportingJobResponse,
  SchemaMap,
  SchemaPreviewResponse,
  DataImportSource,
  ImportingJobPayload,
  ImportFileTypeResponse,
  ImportFileType,
  ImportingJobMetadata
} from '../types'
import { Client, getClient } from 'features/common/utils/clientParameter'

const { workspaceApiUrl, importApiUrl } = settings

export const MAX_FILE_SIZE_TO_IMPORT_IN_BYTES = 5 * 1024 * 1024 * 1024 // 5 GB

export const createImportingJob = async ({
  provisionalJobId,
  user_id,
  file,
  connection,
  destination,
  name,
  autoguessing,
  metadata,
  source,
  schema
}: {
  provisionalJobId: string
  user_id: string
  file: string | File | URL
  connection: string
  destination: string
  name: string
  autoguessing?: boolean
  metadata?: ImportingJobMetadata
  source?: DataImportSource
  schema?: SchemaMap
}): Promise<ImportingJob> => {
  if (file instanceof File) {
    return createImportingJobForFile({
      provisionalJobId,
      user_id,
      file,
      connection,
      destination,
      name,
      autoguessing,
      metadata,
      source
    })
  }
  return createImportingJobForUrl({
    provisionalJobId,
    connection,
    destination,
    name,
    url: file.toString(),
    autoguessing,
    metadata,
    source,
    schema
  })
}

const createImportingJobForFile = async ({
  provisionalJobId,
  user_id,
  file,
  connection,
  destination,
  name,
  autoguessing,
  metadata,
  source
}: {
  provisionalJobId: string
  user_id: string
  file: File
  connection: string
  destination: string
  name: string
  autoguessing?: boolean
  metadata?: ImportingJobMetadata
  source?: DataImportSource
}): Promise<ImportingJob> => {
  const signUrl = `${workspaceApiUrl}/storage/sign`
  const filename = `${user_id}/${uuidv4()}.${file.name.split('.').pop()}`
  const signBody = { type: 'import', filename, connectionName: connection }

  const abortController = getOrCreateJobAbortController(provisionalJobId)

  try {
    const { signal } = abortController
    const { url: putUrl, headers: putHeaders } = await postRequest(signUrl, signBody, { signal })

    await uploadFileRequest({
      url: putUrl,
      headers: putHeaders,
      file,
      options: { maxFileSizeUpload: MAX_FILE_SIZE_TO_IMPORT_IN_BYTES, filename, contentType: ' ' },
      signal
    })

    const { url: getUrl }: { url: string } = await postRequest(signUrl, signBody, { signal })

    return createImportingJobForUrl({
      provisionalJobId,
      connection,
      destination,
      name,
      url: getUrl,
      autoguessing,
      metadata,
      source
    })
  } finally {
    importJobAbortControllers.delete(provisionalJobId)
  }
}

const createImportingJobForUrl = async ({
  provisionalJobId,
  connection,
  destination,
  name,
  url,
  autoguessing,
  metadata,
  source,
  schema
}: {
  provisionalJobId: string
  connection: string
  destination: string
  name: string
  url: string
  autoguessing?: boolean
  metadata?: ImportingJobMetadata
  source?: DataImportSource
  schema?: SchemaMap
}): Promise<ImportingJob> => {
  try {
    const signal = getOrCreateJobAbortController(provisionalJobId).signal
    const createdAt = new Date().toISOString()
    const { jobId, status } = await importRequest({
      connection,
      destination: `${destination}.${name}`,
      url,
      autoguessing,
      metadata,
      source,
      schema
    })

    if (signal.aborted) {
      await cancelImportingJob({ jobId })
      throw new AbortError()
    }
    return { jobId, connection, destination, name, status, metadata, source, createdAt }
  } finally {
    importJobAbortControllers.delete(provisionalJobId)
  }
}

type ImportRequestAPIPayload = {
  connection: string
  destination: string
  url: string
  overwrite: boolean
  autoguessing?: boolean
  metadata?: ImportingJobMetadata
  source?: DataImportSource
  schema?: { columns: { name: string; type: string }[] }
  client: Client
}
type ImportRequestPayload = Omit<ImportRequestAPIPayload, 'overwrite' | 'schema' | 'client'> & { schema?: SchemaMap }

const importRequest = async (payload: ImportRequestPayload) => {
  const body: ImportRequestAPIPayload = {
    ...payload,
    schema: undefined,
    overwrite: true,
    autoguessing: payload.autoguessing ?? true,
    metadata: {
      workflows: payload.metadata?.workflows
    },
    client: getClient()
  }

  // Always send the schema regardless of autoguessing
  if (payload.schema) {
    body.schema = {
      columns: Object.values(payload.schema)
    }
  }

  const { jobId, status } = await postRequest(importApiUrl, body)

  return { jobId, status }
}

export const checkImportJobHasFinished = async ({
  jobId
}: {
  jobId: ImportingJob['jobId']
}): Promise<{ status: JobStatusType; error?: string }> => {
  const url = `${importApiUrl}/${jobId}?client=${getClient()}`
  const importingJob = await getRequest(url)

  return {
    status: importingJob.status,
    error: importingJob.error?.message || importingJob.error
  }
}

export const getImportingJobsList = async (params: ImportingJobPayload): Promise<ImportingJobResponse[]> => {
  return await getRequest(
    `${importApiUrl}?${encodeUriParams({
      user_id: params.owner,
      created_since: params.createdSince,
      status: params.status,
      client: getClient()
    })}`
  )
}

export async function cancelImportingJob({ jobId }: { jobId: string }): Promise<void> {
  const fileUploadController = importJobAbortControllers.get(jobId)
  if (fileUploadController) {
    fileUploadController.abort()
    return
  }

  return await postRequest(`${importApiUrl}/${jobId}/cancel`, {})
}

const importJobAbortControllers: Map<string, AbortController> = new Map()

function getOrCreateJobAbortController(jobId: string) {
  let result = importJobAbortControllers.get(jobId)
  if (result) return result

  result = new AbortController()
  importJobAbortControllers.set(jobId, result)
  return result
}

async function uploadFileToBucket(file: File, userId: string, connectionName?: string) {
  const signUrl = `${workspaceApiUrl}/storage/sign`
  const filename = `${userId}/${uuidv4()}.${file.name.split('.').pop()}`
  const signBody = { type: 'import', filename, connectionName }

  const { url: putUrl, headers: putHeaders } = await postRequest(signUrl, signBody)

  await uploadFileRequest({
    url: putUrl,
    headers: putHeaders,
    file,
    options: { maxFileSizeUpload: MAX_FILE_SIZE_TO_IMPORT_IN_BYTES, filename, contentType: ' ' }
  })

  const { url: getUrl }: { url: string } = await postRequest(signUrl, signBody)
  return getUrl
}

export async function getSchemaPreview({
  file,
  userId,
  connectionName
}: {
  file?: File | URL
  userId?: string
  connectionName?: string
}): Promise<SchemaPreviewResponse> {
  let url
  if (file instanceof File && userId) {
    url = await uploadFileToBucket(file, userId, connectionName)
  } else if (file instanceof URL) {
    url = file.toString()
  }
  const preview = await postRequest(`${importApiUrl}/preview`, { connection: connectionName, url, client: getClient() })
  return { url: new URL(url), preview }
}

export async function getImportFileType({
  file,
  userId
}: {
  file?: File | URL
  userId?: string
}): Promise<ImportFileTypeResponse> {
  if (file instanceof File && file.type) {
    // no need to upload the file just yet
    return { file, fileType: file.type === 'image/tiff' ? ImportFileType.Raster : ImportFileType.Vector }
  }
  let url
  if (file instanceof File && userId) {
    url = await uploadFileToBucket(file, userId)
  } else if (file instanceof URL) {
    url = file.toString()
  }
  const fileTypeResult = await postRequest(`${importApiUrl}/filetype`, { url, client: getClient() })
  return { file: new URL(url), fileType: fileTypeResult.fileType }
}
